import type { SolutionPageContent } from '../../types/solution'

export const accessBusinessFinanceSolution: SolutionPageContent = {
  slug: 'access-business-finance',
  hero: {
    headline: 'Turn your business records into room to grow',
    subheadline: 'The sales and expenses you already keep in Kayko can help you apply for working capital.',
    card: {
      type: 'ring',
      title: 'Business record',
      meta: 'Ingabire Fashion House',
      pct: 86,
      pctLabel: '86%',
      checks: ['142 days of active records', 'Sales and expenses up to date', 'Business details verified'],
      action: 'Check eligibility',
    },
  },
  challenges: [
    {
      quote: 'The bank asks for records I don’t have.',
      detail: 'My business is real and growing, but it’s all in my head and my notebook.',
    },
    {
      quote: 'I miss good deals because I don’t have the cash.',
      detail: 'A supplier offers a discount on bulk stock, and I have to say no.',
    },
    {
      quote: 'Applying for a loan takes weeks of paperwork.',
      detail: 'By the time an answer comes, the opportunity is gone.',
    },
  ],
  helps: [
    {
      solves: 'The bank asks for records I don’t have',
      title: 'A business record that builds itself',
      copy: 'Every sale and expense you record adds to a clear picture of how your business performs over time.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'bars',
          title: 'Monthly revenue',
          value: 'RWF 4.8M',
          delta: { label: '+11%', tone: 'positive' },
          bars: [
            { label: 'Apr', value: 3.6 },
            { label: 'May', value: 3.9 },
            { label: 'Jun', value: 4.1 },
            { label: 'Jul', value: 4.3 },
            { label: 'Aug', value: 4.3 },
            { label: 'Sep', value: 4.8, highlight: true },
          ],
        },
      },
    },
    {
      solves: 'I miss good deals because I don’t have the cash',
      title: 'Know when you may be eligible',
      copy: 'Once you have at least 90 days of active records, see a checklist of what’s complete and what to add next.',
      product: 'business-finance',
      visual: {
        kind: 'card',
        card: {
          type: 'ring',
          title: 'Eligibility checklist',
          pct: 75,
          pctLabel: '3 of 4',
          checks: ['90+ days of active records', 'Sales recorded weekly', 'Expenses recorded monthly'],
          action: 'Complete business details',
        },
      },
    },
    {
      solves: 'Applying takes weeks of paperwork',
      title: 'A simple application, from your workspace',
      copy: 'Apply from Kayko and choose to share your records with our licensed lending partner — no piles of paper.',
      product: 'business-finance',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Application',
          meta: 'Reviewed by Mula Financial Services Ltd',
          rows: [
            { label: 'Business details', value: 'Complete', tone: 'positive' },
            { label: 'Consent to share records', value: 'Given', tone: 'positive' },
            { label: 'Purpose of financing', value: 'Stock purchase' },
          ],
          action: 'Submit application',
        },
      },
    },
  ],
  journey: {
    heading: 'How it works',
    subheading: 'From daily records to an application.',
    items: [
      { title: 'Use Kayko every day', copy: 'Record sales and expenses as you work.' },
      { title: 'Build 90 days of records', copy: 'Your business record grows automatically.' },
      { title: 'Check your checklist', copy: 'See what’s complete and what to add.' },
      { title: 'Apply with consent', copy: 'Share your records with our lending partner.' },
      { title: 'Get a decision', copy: 'The lender reviews and contacts you.' },
    ],
  },
  results: [
    { value: '90 days', label: 'of active records to start an application' },
    { value: '0', label: 'paper documents needed to apply from Kayko' },
    { value: '100%', label: 'of your records shared only with your consent' },
  ],
  testimonial: {
    quote: 'My records in Kayko showed how my shop really performs. For the first time, I could apply for financing with confidence.',
    name: 'Solange Ingabire',
    role: 'Owner',
    business: 'Ingabire Fashion House',
    location: 'Remera, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/55a948a8-f1a2-4b85-9533-52b1fa01fe2f.jpg',
    metric: { value: '142', label: 'days of business records built in Kayko' },
  },
  products: ['business-finance', 'reports', 'expenses', 'sales-pos'],
  plan: { name: 'Starter', audience: 'businesses preparing to apply for financing' },
  faq: [
    {
      question: 'Who provides the financing?',
      answer: 'Business Financing is provided by Mula Financial Services Ltd. Financing is subject to eligibility and approval.',
    },
    {
      question: 'Who can apply?',
      answer: 'Registered Kayko businesses with at least 90 days of active business records.',
    },
    {
      question: 'What records are used?',
      answer: 'Your sales, expenses, and business details in Kayko — shared only when you give consent.',
    },
    {
      question: 'Does using Kayko guarantee approval?',
      answer: 'No. The lender makes the final decision based on its own assessment.',
    },
    {
      question: 'Can I join before financing opens for my business?',
      answer: 'Yes. Join early access and we’ll contact you when Business Financing is available to you.',
      link: { label: 'Join early access', href: '/business-finance/apply' },
    },
  ],
  related: ['control-expenses', 'understand-performance', 'retail'],
  cta: {
    headline: 'Start building your business record today',
    subheadline: 'Financing is subject to eligibility and approval. Provided by Mula Financial Services Ltd.',
  },
}
